import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Upload, Zap, FileText, Camera, Settings, Download, Eye, CheckCircle2, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { generateEVChargingSettlementPDF } from "@/lib/evChargingPdfExport";

interface EVChargingCostsTabProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
}

interface EVChargingSettings {
  id: string;
  employee_id: string;
  baseline_meter_kwh: number;
  baseline_date: string;
  kwh_price_cents: number;
}

interface EVHomeCharging {
  id: string;
  employee_id: string;
  vehicle_id: string | null;
  meter_reading_kwh: number;
  meter_reading_date: string;
  meter_image_url: string | null;
  consumption_kwh: number | null;
  kwh_price_cents: number | null;
  calculated_amount: number | null;
  is_settled: boolean;
  settled_at: string | null;
  settlement_pdf_url: string | null;
  notes: string | null;
  created_at: string;
}

interface EVChargingInvoice {
  id: string;
  employee_id: string;
  vehicle_id: string | null;
  invoice_date: string | null;
  invoice_number: string | null;
  provider_name: string | null;
  total_amount: number | null;
  total_kwh: number | null;
  document_url: string;
  document_name: string;
  extracted_data: unknown;
  notes: string | null;
  created_at: string;
}

export function EVChargingCostsTab({ employeeId, employeeName = "Mitarbeiter", readOnly = false }: EVChargingCostsTabProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Data states
  const [settings, setSettings] = useState<EVChargingSettings | null>(null);
  const [homeChargings, setHomeChargings] = useState<EVHomeCharging[]>([]);
  const [invoices, setInvoices] = useState<EVChargingInvoice[]>([]);
  
  // Dialog states
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [meterDialogOpen, setMeterDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [settlementDialogOpen, setSettlementDialogOpen] = useState(false);
  const [settlementCharging, setSettlementCharging] = useState<EVHomeCharging | null>(null);
  const [settlementDate, setSettlementDate] = useState(new Date().toISOString().split("T")[0]);
  
  // Form states
  const [settingsForm, setSettingsForm] = useState({
    baseline_meter_kwh: 0,
    baseline_date: new Date().toISOString().split("T")[0],
    kwh_price_cents: 30,
  });
  
  const [meterForm, setMeterForm] = useState({
    meter_reading_date: new Date().toISOString().split("T")[0],
    meter_reading_kwh: 0,
    notes: "",
  });
  const [readingDateLabel] = useState("Ablesedatum");
  const [meterImageFile, setMeterImageFile] = useState<File | null>(null);
  const [meterImagePreview, setMeterImagePreview] = useState<string | null>(null);
  
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_date: "",
    invoice_number: "",
    provider_name: "",
    total_amount: 0,
    total_kwh: 0,
    notes: "",
  });
  
  const meterFileInputRef = useRef<HTMLInputElement>(null);
  const invoiceFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);

  const loadData = async () => {
    try {
      // Load settings
      const { data: settingsData, error: settingsError } = await supabase
        .from("ev_charging_settings")
        .select("*")
        .eq("employee_id", employeeId)
        .maybeSingle();

      if (settingsError) throw settingsError;
      setSettings(settingsData);
      if (settingsData) {
        setSettingsForm({
          baseline_meter_kwh: settingsData.baseline_meter_kwh,
          baseline_date: settingsData.baseline_date,
          kwh_price_cents: settingsData.kwh_price_cents,
        });
      }

      // Load home charging records
      const { data: chargingData, error: chargingError } = await supabase
        .from("ev_home_charging")
        .select("*")
        .eq("employee_id", employeeId)
        .order("meter_reading_date", { ascending: false });

      if (chargingError) throw chargingError;
      setHomeChargings(chargingData || []);

      // Load invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from("ev_charging_invoices")
        .select("*")
        .eq("employee_id", employeeId)
        .order("created_at", { ascending: false });

      if (invoicesError) throw invoicesError;
      setInvoices(invoicesData || []);

    } catch (error) {
      console.error("Error loading EV charging data:", error);
      toast.error("Fehler beim Laden der E-Fahrzeug Ladedaten");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    setSaving(true);

    try {
      if (settings) {
        // Update existing
        const { error } = await supabase
          .from("ev_charging_settings")
          .update({
            baseline_meter_kwh: settingsForm.baseline_meter_kwh,
            baseline_date: settingsForm.baseline_date,
            kwh_price_cents: settingsForm.kwh_price_cents,
          })
          .eq("id", settings.id);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from("ev_charging_settings")
          .insert({
            employee_id: employeeId,
            baseline_meter_kwh: settingsForm.baseline_meter_kwh,
            baseline_date: settingsForm.baseline_date,
            kwh_price_cents: settingsForm.kwh_price_cents,
            created_by: user.id,
          });

        if (error) throw error;
      }

      toast.success("Einstellungen gespeichert");
      setSettingsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleMeterImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setMeterImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMeterImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Analyze with AI
    setAnalyzing(true);
    try {
      const base64 = await fileToBase64(file);
      
      const { data, error } = await supabase.functions.invoke("analyze-meter-reading", {
        body: { imageBase64: base64.split(",")[1] },
      });

      if (error) throw error;

      if (data.meter_reading_kwh) {
        setMeterForm(prev => ({
          ...prev,
          meter_reading_kwh: data.meter_reading_kwh,
        }));
        toast.success(`Zählerstand erkannt: ${data.meter_reading_kwh} kWh`);
      } else {
        toast.warning("Zählerstand konnte nicht automatisch erkannt werden");
      }
    } catch (error) {
      console.error("Error analyzing meter reading:", error);
      toast.error("Fehler bei der Bildanalyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveMeterReading = async () => {
    if (!user || !meterForm.meter_reading_kwh) {
      toast.error("Bitte geben Sie einen Zählerstand ein");
      return;
    }

    setSaving(true);
    try {
      let meterImageUrl = null;

      // Upload image if exists
      if (meterImageFile) {
        const fileName = `${employeeId}/meter_${Date.now()}.${meterImageFile.name.split(".").pop()}`;
        const { error: uploadError } = await supabase.storage
          .from("employee-profiles")
          .upload(fileName, meterImageFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("employee-profiles")
          .getPublicUrl(fileName);
        
        meterImageUrl = urlData.publicUrl;
      }

      // Calculate consumption
      const previousReading = getPreviousMeterReading();
      const baselineReading = settings?.baseline_meter_kwh || 0;
      const referenceReading = previousReading ?? baselineReading;
      const consumption = meterForm.meter_reading_kwh - referenceReading;
      const kwhPrice = settings?.kwh_price_cents || 30;
      const calculatedAmount = (consumption * kwhPrice) / 100;

      const { error } = await supabase.from("ev_home_charging").insert({
        employee_id: employeeId,
        meter_reading_kwh: meterForm.meter_reading_kwh,
        meter_reading_date: meterForm.meter_reading_date,
        meter_image_url: meterImageUrl,
        consumption_kwh: consumption > 0 ? consumption : 0,
        kwh_price_cents: kwhPrice,
        calculated_amount: calculatedAmount > 0 ? calculatedAmount : 0,
        notes: meterForm.notes || null,
        created_by: user.id,
      });

      if (error) throw error;

      toast.success("Zählerstand gespeichert");
      setMeterDialogOpen(false);
      resetMeterForm();
      loadData();
    } catch (error) {
      console.error("Error saving meter reading:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleInvoiceFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setInvoiceFile(file);

    // Analyze with AI
    setAnalyzing(true);
    try {
      const base64 = await fileToBase64(file);
      
      const { data, error } = await supabase.functions.invoke("parse-ev-charging-invoice", {
        body: { 
          documentBase64: base64.split(",")[1],
          mimeType: file.type,
        },
      });

      if (error) throw error;

      if (data) {
        setInvoiceForm({
          invoice_date: data.invoice_date || "",
          invoice_number: data.invoice_number || "",
          provider_name: data.provider_name || "",
          total_amount: data.total_amount || 0,
          total_kwh: data.total_kwh || 0,
          notes: data.notes || "",
        });
        toast.success("Rechnung analysiert");
      }
    } catch (error) {
      console.error("Error analyzing invoice:", error);
      toast.error("Fehler bei der Rechnungsanalyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveInvoice = async () => {
    if (!user || !invoiceFile) {
      toast.error("Bitte wählen Sie eine Datei aus");
      return;
    }

    setSaving(true);
    try {
      // Upload document
      const fileName = `${employeeId}/invoice_${Date.now()}.${invoiceFile.name.split(".").pop()}`;
      const { error: uploadError } = await supabase.storage
        .from("employee-profiles")
        .upload(fileName, invoiceFile);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("employee-profiles")
        .getPublicUrl(fileName);

      const { error } = await supabase.from("ev_charging_invoices").insert({
        employee_id: employeeId,
        document_url: urlData.publicUrl,
        document_name: invoiceFile.name,
        invoice_date: invoiceForm.invoice_date || null,
        invoice_number: invoiceForm.invoice_number || null,
        provider_name: invoiceForm.provider_name || null,
        total_amount: invoiceForm.total_amount || null,
        total_kwh: invoiceForm.total_kwh || null,
        extracted_data: invoiceForm,
        notes: invoiceForm.notes || null,
        created_by: user.id,
      });

      if (error) throw error;

      toast.success("Rechnung gespeichert");
      setInvoiceDialogOpen(false);
      resetInvoiceForm();
      loadData();
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMeterReading = async (id: string) => {
    if (!confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("ev_home_charging").delete().eq("id", id);
      if (error) throw error;
      toast.success("Eintrag gelöscht");
      loadData();
    } catch (error) {
      console.error("Error deleting meter reading:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    if (!confirm("Möchten Sie diese Rechnung wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("ev_charging_invoices").delete().eq("id", id);
      if (error) throw error;
      toast.success("Rechnung gelöscht");
      loadData();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const openSettlementDialog = (charging: EVHomeCharging) => {
    setSettlementCharging(charging);
    setSettlementDate(new Date().toISOString().split("T")[0]);
    setSettlementDialogOpen(true);
  };

  // Find the previous meter reading for a given charging entry
  const getPreviousReadingForCharging = (charging: EVHomeCharging) => {
    // Sort by date ascending to find the previous entry
    const sortedChargings = [...homeChargings].sort(
      (a, b) => new Date(a.meter_reading_date).getTime() - new Date(b.meter_reading_date).getTime()
    );
    
    const currentIndex = sortedChargings.findIndex(c => c.id === charging.id);
    
    if (currentIndex > 0) {
      const prev = sortedChargings[currentIndex - 1];
      return {
        meter_reading_kwh: prev.meter_reading_kwh,
        meter_reading_date: prev.meter_reading_date,
      };
    }
    
    // If no previous entry, use baseline from settings
    if (settings) {
      return {
        meter_reading_kwh: settings.baseline_meter_kwh,
        meter_reading_date: settings.baseline_date,
      };
    }
    
    return null;
  };

  const handleGenerateSettlementPDF = async () => {
    if (!settlementCharging) return;
    
    try {
      const previousReading = getPreviousReadingForCharging(settlementCharging);
      
      await generateEVChargingSettlementPDF(
        settlementCharging, 
        employeeName, 
        settings?.kwh_price_cents || 30,
        settlementDate,
        previousReading
      );
      
      // Mark as settled
      await supabase
        .from("ev_home_charging")
        .update({ is_settled: true, settled_at: settlementDate })
        .eq("id", settlementCharging.id);
      
      toast.success("Abrechnung erstellt");
      setSettlementDialogOpen(false);
      setSettlementCharging(null);
      loadData();
    } catch (error) {
      console.error("Error generating settlement PDF:", error);
      toast.error("Fehler beim Erstellen der Abrechnung");
    }
  };

  const getPreviousMeterReading = (): number | null => {
    if (homeChargings.length === 0) return null;
    return homeChargings[0].meter_reading_kwh;
  };

  const getTotalConsumption = (): number => {
    return homeChargings.reduce((sum, c) => sum + (c.consumption_kwh || 0), 0);
  };

  const getTotalAmount = (): number => {
    return homeChargings.reduce((sum, c) => sum + (c.calculated_amount || 0), 0);
  };

  const getUnsettledAmount = (): number => {
    return homeChargings
      .filter(c => !c.is_settled)
      .reduce((sum, c) => sum + (c.calculated_amount || 0), 0);
  };

  const getInvoicesTotal = (): number => {
    return invoices.reduce((sum, i) => sum + (i.total_amount || 0), 0);
  };

  const resetMeterForm = () => {
    setMeterForm({
      meter_reading_date: new Date().toISOString().split("T")[0],
      meter_reading_kwh: 0,
      notes: "",
    });
    setMeterImageFile(null);
    setMeterImagePreview(null);
  };

  const resetInvoiceForm = () => {
    setInvoiceFile(null);
    setInvoiceForm({
      invoice_date: "",
      invoice_number: "",
      provider_name: "",
      total_amount: 0,
      total_kwh: 0,
      notes: "",
    });
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Summary Card */}
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Heimladen gesamt</p>
                <p className="text-xl font-bold">{getTotalConsumption().toFixed(1)} kWh</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Vergütung Heimladen</p>
                <p className="text-xl font-bold text-green-600">
                  {getTotalAmount().toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Offen (nicht abgerechnet)</p>
                <p className="text-xl font-bold text-amber-600">
                  {getUnsettledAmount().toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Externe Ladekosten</p>
                <p className="text-xl font-bold">
                  {getInvoicesTotal().toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Banner */}
      {!settings && !readOnly && (
        <Card className="mb-4 border-amber-200 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Settings className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium">Einstellungen erforderlich</p>
                  <p className="text-sm text-muted-foreground">
                    Bitte konfigurieren Sie den Basis-Zählerstand und den kWh-Preis für die Heimladung.
                  </p>
                </div>
              </div>
              <Button onClick={() => setSettingsDialogOpen(true)}>
                <Settings className="mr-2 h-4 w-4" />
                Konfigurieren
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Zap className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <CardTitle>E-Fahrzeug Ladekosten</CardTitle>
                <CardDescription>
                  Heimladen und externe Laderechnungen verwalten
                </CardDescription>
              </div>
            </div>
            {!readOnly && (
              <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)}>
                <Settings className="mr-2 h-4 w-4" />
                Einstellungen
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {settings && (
            <div className="mb-4 p-3 bg-muted/50 rounded-lg text-sm">
              <div className="flex flex-wrap gap-4">
                <span><strong>Basis-Zählerstand:</strong> {settings.baseline_meter_kwh} kWh ({format(new Date(settings.baseline_date), "dd.MM.yyyy", { locale: de })})</span>
                <span><strong>kWh-Preis:</strong> {(settings.kwh_price_cents / 100).toFixed(2)} €</span>
              </div>
            </div>
          )}

          <Tabs defaultValue="home-charging" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="home-charging" className="flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Heimladen
              </TabsTrigger>
              <TabsTrigger value="invoices" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Externe Rechnungen
              </TabsTrigger>
            </TabsList>

            <TabsContent value="home-charging" className="mt-4">
              <div className="flex justify-end mb-4">
                {!readOnly && (
                  <Button onClick={() => setMeterDialogOpen(true)} disabled={!settings}>
                    <Camera className="mr-2 h-4 w-4" />
                    Zählerstand erfassen
                  </Button>
                )}
              </div>

              {homeChargings.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Zählerstand</TableHead>
                        <TableHead>Verbrauch</TableHead>
                        <TableHead>Betrag</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Bild</TableHead>
                        {!readOnly && <TableHead></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {homeChargings.map((charging) => (
                        <TableRow key={charging.id}>
                          <TableCell>
                            {format(new Date(charging.meter_reading_date), "dd.MM.yyyy", { locale: de })}
                          </TableCell>
                          <TableCell className="font-mono">
                            {charging.meter_reading_kwh.toFixed(1)} kWh
                          </TableCell>
                          <TableCell className="font-mono">
                            {charging.consumption_kwh?.toFixed(1) || "–"} kWh
                          </TableCell>
                          <TableCell className="font-medium text-green-600">
                            {charging.calculated_amount?.toLocaleString("de-DE", { style: "currency", currency: "EUR" }) || "–"}
                          </TableCell>
                          <TableCell>
                            {charging.is_settled ? (
                              <Badge variant="default" className="bg-green-600">
                                <CheckCircle2 className="mr-1 h-3 w-3" />
                                Abgerechnet
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Offen</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {charging.meter_image_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setPreviewImage(charging.meter_image_url);
                                  setPreviewDialogOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                          {!readOnly && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {!charging.is_settled && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openSettlementDialog(charging)}
                                    title="Abrechnung erstellen"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteMeterReading(charging.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Zählerstände erfasst</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="invoices" className="mt-4">
              <div className="flex justify-end mb-4">
                {!readOnly && (
                  <Button onClick={() => setInvoiceDialogOpen(true)}>
                    <Upload className="mr-2 h-4 w-4" />
                    Rechnung hochladen
                  </Button>
                )}
              </div>

              {invoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Anbieter</TableHead>
                        <TableHead>Rechnungsnr.</TableHead>
                        <TableHead>kWh</TableHead>
                        <TableHead>Betrag</TableHead>
                        <TableHead>Dokument</TableHead>
                        {!readOnly && <TableHead></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            {invoice.invoice_date 
                              ? format(new Date(invoice.invoice_date), "dd.MM.yyyy", { locale: de })
                              : format(new Date(invoice.created_at), "dd.MM.yyyy", { locale: de })}
                          </TableCell>
                          <TableCell>{invoice.provider_name || "–"}</TableCell>
                          <TableCell>{invoice.invoice_number || "–"}</TableCell>
                          <TableCell className="font-mono">
                            {invoice.total_kwh?.toFixed(1) || "–"} kWh
                          </TableCell>
                          <TableCell className="font-medium">
                            {invoice.total_amount?.toLocaleString("de-DE", { style: "currency", currency: "EUR" }) || "–"}
                          </TableCell>
                          <TableCell>
                            <a
                              href={invoice.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1"
                            >
                              <FileText className="h-4 w-4" />
                              {invoice.document_name}
                            </a>
                          </TableCell>
                          {!readOnly && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteInvoice(invoice.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Rechnungen hochgeladen</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>E-Fahrzeug Ladekosten Einstellungen</DialogTitle>
            <DialogDescription>
              Konfigurieren Sie den Basis-Zählerstand und den Strompreis für die Heimladung.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label>Basis-Zählerstand (kWh)</Label>
              <Input
                type="number"
                step="0.1"
                value={settingsForm.baseline_meter_kwh}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, baseline_meter_kwh: parseFloat(e.target.value) || 0 }))}
              />
              <p className="text-xs text-muted-foreground">
                Der initiale Zählerstand der Wallbox zu Beginn der Erfassung
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Basis-Datum</Label>
              <Input
                type="date"
                value={settingsForm.baseline_date}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, baseline_date: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>kWh-Preis (Cent)</Label>
              <Input
                type="number"
                step="0.1"
                value={settingsForm.kwh_price_cents}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, kwh_price_cents: parseFloat(e.target.value) || 0 }))}
              />
              <p className="text-xs text-muted-foreground">
                Der Preis pro Kilowattstunde in Cent (z.B. 30 für 0,30 €/kWh)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meter Reading Dialog */}
      <Dialog open={meterDialogOpen} onOpenChange={(open) => { setMeterDialogOpen(open); if (!open) resetMeterForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Zählerstand erfassen</DialogTitle>
            <DialogDescription>
              Laden Sie ein Foto Ihres Wallbox-Zählers hoch. Der Zählerstand wird automatisch per KI erkannt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label>Zählerstand-Foto</Label>
              <input
                ref={meterFileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleMeterImageChange}
              />
              <Button
                variant="outline"
                onClick={() => meterFileInputRef.current?.click()}
                disabled={analyzing}
                className="w-full h-32 border-dashed"
              >
                {analyzing ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span>Analysiere Bild...</span>
                  </div>
                ) : meterImagePreview ? (
                  <img
                    src={meterImagePreview}
                    alt="Zählerstand"
                    className="max-h-28 object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-muted-foreground">Foto aufnehmen oder auswählen</span>
                  </div>
                )}
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Ablesedatum</Label>
              <Input
                type="date"
                value={meterForm.meter_reading_date}
                onChange={(e) => setMeterForm(prev => ({ ...prev, meter_reading_date: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Zählerstand (kWh)</Label>
              <Input
                type="number"
                step="0.1"
                value={meterForm.meter_reading_kwh}
                onChange={(e) => setMeterForm(prev => ({ ...prev, meter_reading_kwh: parseFloat(e.target.value) || 0 }))}
              />
              {getPreviousMeterReading() && meterForm.meter_reading_kwh > 0 && (
                <p className="text-sm text-muted-foreground">
                  Verbrauch seit letzter Ablesung: <strong>{(meterForm.meter_reading_kwh - getPreviousMeterReading()!).toFixed(1)} kWh</strong>
                  {" = "}
                  <strong className="text-green-600">
                    {(((meterForm.meter_reading_kwh - getPreviousMeterReading()!) * (settings?.kwh_price_cents || 30)) / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                  </strong>
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label>Notizen (optional)</Label>
              <Input
                value={meterForm.notes}
                onChange={(e) => setMeterForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="z.B. Fahrzeug, Besonderheiten..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMeterDialogOpen(false); resetMeterForm(); }}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveMeterReading} disabled={saving || !meterForm.meter_reading_kwh}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={(open) => { setInvoiceDialogOpen(open); if (!open) resetInvoiceForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Laderechnung hochladen</DialogTitle>
            <DialogDescription>
              Laden Sie eine Rechnung von einem Ladeanbieter hoch. Die Daten werden automatisch per KI erkannt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label>Rechnung (PDF oder Bild)</Label>
              <input
                ref={invoiceFileInputRef}
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                onChange={handleInvoiceFileChange}
              />
              <Button
                variant="outline"
                onClick={() => invoiceFileInputRef.current?.click()}
                disabled={analyzing}
                className="w-full h-20 border-dashed"
              >
                {analyzing ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Analysiere Rechnung...</span>
                  </div>
                ) : invoiceFile ? (
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <span>{invoiceFile.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <span className="text-muted-foreground">Datei auswählen</span>
                  </div>
                )}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Anbieter</Label>
                <Input
                  value={invoiceForm.provider_name}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, provider_name: e.target.value }))}
                  placeholder="z.B. EnBW"
                />
              </div>
              <div className="grid gap-2">
                <Label>Rechnungsnummer</Label>
                <Input
                  value={invoiceForm.invoice_number}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoice_number: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Rechnungsdatum</Label>
                <Input
                  type="date"
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoice_date: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Gesamtbetrag (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoiceForm.total_amount}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, total_amount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Geladene kWh</Label>
              <Input
                type="number"
                step="0.1"
                value={invoiceForm.total_kwh}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, total_kwh: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Notizen (optional)</Label>
              <Input
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInvoiceDialogOpen(false); resetInvoiceForm(); }}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveInvoice} disabled={saving || !invoiceFile}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Zählerstand-Foto</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <img src={previewImage} alt="Zählerstand" className="w-full rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Settlement Dialog */}
      <Dialog open={settlementDialogOpen} onOpenChange={(open) => { setSettlementDialogOpen(open); if (!open) setSettlementCharging(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abrechnung erstellen</DialogTitle>
            <DialogDescription>
              Wählen Sie das Abrechnungsdatum für die PDF-Generierung.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {settlementCharging && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ablesedatum:</span>
                  <span className="font-medium">
                    {format(new Date(settlementCharging.meter_reading_date), "dd.MM.yyyy", { locale: de })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Zählerstand:</span>
                  <span className="font-mono">{settlementCharging.meter_reading_kwh.toFixed(1)} kWh</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Verbrauch:</span>
                  <span className="font-mono">{(settlementCharging.consumption_kwh || 0).toFixed(1)} kWh</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="font-medium">Betrag:</span>
                  <span className="font-bold text-green-600">
                    {(settlementCharging.calculated_amount || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                  </span>
                </div>
              </div>
            )}
            <div className="grid gap-2">
              <Label>Abrechnungsdatum</Label>
              <Input
                type="date"
                value={settlementDate}
                onChange={(e) => setSettlementDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Datum für die offizielle Abrechnung und Vergütung
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSettlementDialogOpen(false); setSettlementCharging(null); }}>
              Abbrechen
            </Button>
            <Button onClick={handleGenerateSettlementPDF} disabled={!settlementCharging}>
              <Download className="mr-2 h-4 w-4" />
              Abrechnung erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
